import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SearchIcon from '@mui/icons-material/Search';
import Icon from '../tools/Icon';

const styles = {
  container: {
    width: '100%',
    overflowY: 'auto',
    px: { xs: 3, md: 5 },
    pt: 1.5,
    pb: 2,
  },
  skeletonContainer: {
    px: { xs: 3, md: 5 },
    pt: 1.5,
    pb: 2,
  },
  skeletonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    py: 2,
  },
  skeletonContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  emptyWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 8,
    px: 3,
    gap: 2,
  },
  emptyIconBox: {
    width: 88,
    height: 88,
    borderRadius: 4,
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  emptyBgIcon: {
    fontSize: 52,
    color: 'text.disabled',
  },
  emptyFgIcon: {
    fontSize: 28,
    color: 'text.secondary',
    position: 'absolute',
    bottom: 12,
    right: 10,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: 'text.primary',
    textAlign: 'center',
  },
  emptySubtitle: {
    fontSize: 13,
    color: 'text.secondary',
    textAlign: 'center',
    maxWidth: 280,
  },
  resultsCountBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: { xs: 3, md: 5 },
    pt: 0.75,
    pb: 0.5,
  },
  resultsCountLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'text.secondary',
  },
  resultsCountRight: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0.3,
  },
  resultsCountCurrent: {
    fontSize: 12,
    fontWeight: 700,
    color: 'text.primary',
  },
  resultsCountTotal: {
    fontSize: 11,
    fontWeight: 400,
    color: 'text.secondary',
  },
  progressTrack: {
    mx: { xs: 3, md: 5 },
    height: 10,
    minHeight: 10,
    borderRadius: 9999,
    bgcolor: theme => theme.bruno.colors.inactive,
    overflow: 'hidden',
    mb: 0.75,
  },
  progressFill: {
    height: '100%',
    borderRadius: 9999,
    bgcolor: 'info.main',
    opacity: 0.6,
  },
  resultsBarDivider: {
    borderColor: 'divider',
    mx: { xs: 3, md: 5 },
    mt: 0.75,
  },
  // Card row
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    py: 2,
    px: 1,
    mx: -1,
    borderRadius: 2,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { bgcolor: 'action.hover' },
    '&:hover .product-title': { color: 'primary.main' },
  },
  // Left icon box
  productIconBox: {
    width: 48,
    height: 48,
    borderRadius: 2,
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  productIcon: {
    fontSize: 22,
    color: 'text.secondary',
  },
  // Center content grid
  cardContent: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    gap: 2,
    alignItems: 'center',
  },
  productName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'text.primary',
    lineHeight: 1.3,
    transition: 'color 0.15s',
  },
  metaColumns: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: 1, sm: 3, lg: 4 },
  },
  metaBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  metaLabel: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'text.secondary',
  },
  metaValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  metaValueItem: {
    fontSize: 12,
    fontWeight: 700,
    color: 'info.main',
  },
  metaValueBarcode: {
    fontSize: 12,
    fontWeight: 600,
    color: 'text.primary',
    letterSpacing: '-0.2px',
  },
  iconSq: {
    mx: 0,
  },
  chevron: {
    fontSize: 18,
    color: 'text.disabled',
    flexShrink: 0,
  },
};

const PAGE_SIZE = 10;
const SKELETON_CARD_HEIGHT = 73; // icon 48px + py*2 (16px each) + divider ~1px
const HEADER_OFFSET = 240; // searchHeader + resultsBar + progressTrack + divider + safety margin

function useSkeletonCount() {
  const [count, setCount] = React.useState(() =>
    Math.max(3, Math.floor((window.innerHeight - HEADER_OFFSET) / SKELETON_CARD_HEIGHT)),
  );
  useEffect(() => {
    const update = () =>
      setCount(Math.max(3, Math.floor((window.innerHeight - HEADER_OFFSET) / SKELETON_CARD_HEIGHT)));
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return count;
}

export default function ProductsResults({
  searchResults,
  searchResultsFilter,
  loadingSearch,
  hasSearched,
  setSearchFilter,
  resetSearchFilter,
  onProductSelect,
}) {
  const { t } = useTranslation();
  const skeletonCount = useSkeletonCount();

  useEffect(() => {
    resetSearchFilter();
    setSearchFilter(0);
  }, [searchResults, resetSearchFilter, setSearchFilter]);

  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 80;
      if (nearBottom && !loadingSearch) {
        const currentPage = Math.floor(searchResultsFilter.length / PAGE_SIZE);
        if (searchResultsFilter.length < searchResults.length) {
          setSearchFilter(currentPage);
        }
      }
    },
    [loadingSearch, searchResultsFilter.length, searchResults.length, setSearchFilter],
  );

  const handleSelectProduct = useCallback(
    (product) => {
      if (onProductSelect) onProductSelect(product);
    },
    [onProductSelect],
  );

  const showResults = !loadingSearch && searchResultsFilter.length > 0;
  const progress = searchResults.length > 0
    ? (searchResultsFilter.length / searchResults.length) * 100
    : 0;
  const hasMore = useMemo(
    () => searchResultsFilter.length < searchResults.length,
    [searchResultsFilter.length, searchResults.length],
  );

  return (
    <>
      {loadingSearch ? (
        <Box sx={styles.resultsCountBar}>
          <Typography sx={styles.resultsCountLabel}>
            {t('bruno.products.results_title', 'Results')}
          </Typography>
          <Box sx={styles.resultsCountRight}>
            <Skeleton variant="text" width={30} height={16} />
          </Box>
        </Box>
      ) : (
        <Box sx={styles.resultsCountBar}>
          <Typography sx={styles.resultsCountLabel}>
            {t('bruno.products.results_title', 'Results')}
          </Typography>
          <Box sx={styles.resultsCountRight}>
            <Typography sx={styles.resultsCountCurrent}>
              {hasSearched ? searchResultsFilter.length : '0'}
            </Typography>
            <Typography sx={styles.resultsCountTotal}>
              {hasSearched ? `/ ${searchResults.length}` : '/ 0'}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={styles.progressTrack}>
        {loadingSearch ? (
          <Skeleton variant="rounded" width="100%" height={10} />
        ) : (
          <Box sx={{ ...styles.progressFill, width: `${progress}%` }} />
        )}
      </Box>
      <Divider sx={styles.resultsBarDivider} />

      {loadingSearch && (
        <Box sx={styles.skeletonContainer}>
          {[...Array(skeletonCount)].map((_, i) => (
            <React.Fragment key={i}>
              <Box sx={styles.skeletonCard}>
                <Skeleton variant="rounded" width={48} height={48} />
                <Box sx={styles.skeletonContent}>
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton variant="text" width="25%" height={14} />
                </Box>
                <Skeleton variant="text" width={18} height={18} />
              </Box>
              {i < skeletonCount - 1 && <Divider sx={{ borderColor: '#f3f4f5' }} />}
            </React.Fragment>
          ))}
        </Box>
      )}

      {!loadingSearch && hasSearched && searchResultsFilter.length === 0 && (
        <Box sx={styles.emptyWrapper}>
          <Box sx={styles.emptyIconBox}>
            <SearchOffIcon sx={styles.emptyBgIcon} />
          </Box>
          <Typography sx={styles.emptyTitle}>
            {t('bruno.products.empty_title', 'No results found')}
          </Typography>
          <Typography sx={styles.emptySubtitle}>
            {t('bruno.products.empty_subtitle', 'Try different terms or use Scan to find the product quickly')}
          </Typography>
        </Box>
      )}

      {!hasSearched && searchResultsFilter.length === 0 && (
        <Box sx={styles.emptyWrapper}>
          <Box sx={styles.emptyIconBox}>
            <SearchIcon sx={styles.emptyBgIcon} />
          </Box>
          <Typography sx={styles.emptyTitle}>
            {t('bruno.products.empty_initial_title', 'Search for a product')}
          </Typography>
          <Typography sx={styles.emptySubtitle}>
            {t('bruno.products.empty_initial_subtitle', 'Enter a name, item ID or barcode to get started')}
          </Typography>
        </Box>
      )}

      {showResults && (
        <Box sx={styles.container} onScroll={handleScroll}>
          {searchResultsFilter.map((product, index) => (
            <React.Fragment key={`${product.item}-${product.ean}-${index}`}>
              <Box sx={styles.card} onClick={() => handleSelectProduct(product)}>
                <Box sx={styles.productIconBox}>
                  <InventoryIcon sx={styles.productIcon} />
                </Box>
                <Box sx={styles.cardContent}>
                  <Typography className="product-title" sx={styles.productName}>
                    {product.description}
                  </Typography>
                  <Box sx={styles.metaColumns}>
                    <Box sx={styles.metaBlock}>
                      <Typography sx={styles.metaLabel}>
                        {t('bruno.products.item')}
                      </Typography>
                      <Box sx={styles.metaValueRow}>
                        <Icon
                          materialIcon="label"
                          hexColor="#0070B7"
                          variant="filled"
                          fontSize="16px"
                          sx={styles.iconSq}
                        />
                        <Typography sx={styles.metaValueItem}>
                          #{product.item}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={styles.metaBlock}>
                      <Typography sx={styles.metaLabel}>
                        {t('bruno.products.barcode')}
                      </Typography>
                      <Box sx={styles.metaValueRow}>
                        <Icon
                          materialIcon="qr_code_2"
                          hexColor="#0070B7"
                          variant="filled"
                          fontSize="16px"
                          sx={styles.iconSq}
                        />
                        <Typography sx={styles.metaValueBarcode}>
                          {product.ean}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <ChevronRightIcon sx={styles.chevron} />
              </Box>
              {index < searchResultsFilter.length - 1 && (
                <Divider sx={{ borderColor: '#f3f4f5' }} />
              )}
            </React.Fragment>
          ))}

          {hasSearched && (
            <Box sx={{ py: 2.5, textAlign: 'center' }}>
              {loadingSearch ? (
                <CircularProgress size={22} thickness={4} />
              ) : !hasMore ? (
                <Typography variant="caption" color="text.disabled" sx={{ letterSpacing: 2, textTransform: 'uppercase' }}>
                  {t('bruno.products.list_end', 'There are no more products')}
                </Typography>
              ) : (
                <Box sx={{ height: 1 }} />
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
